import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { QASkeleton } from './QASkeleton';
import { QAEmpty } from './QAEmpty';
import { formatAUD, formatDateAU } from '../../../shared/format';
import { COVER_LABEL, COVER_TYPES, PREMIUM_FREQUENCIES, PREMIUM_FREQ_LABEL, type CoverType, type PremiumFrequency } from '../../../shared/insurance';

interface Policy {
  id: string;
  memberId: string | null;
  lifeInsured: string;
  coverType: CoverType;
  insurer: string | null;
  policyNumber: string | null;
  sumInsuredCents: number | null;
  premiumCents: number | null;
  premiumFrequency: PremiumFrequency | null;
  heldInSuper: boolean;
  commencedDate: string | null;
  notes: string | null;
  nextAnniversary: string | null;
  annualPremium: number | null;
}
interface Member { id: string; role: string; firstName?: string | null; surname?: string | null }

type Draft = {
  memberId: string; lifeInsured: string; coverType: CoverType; insurer: string; policyNumber: string;
  sumInsured: string; premium: string; premiumFrequency: PremiumFrequency; heldInSuper: boolean; commencedDate: string; notes: string;
};
const EMPTY: Draft = { memberId: '', lifeInsured: '', coverType: 'life', insurer: '', policyNumber: '', sumInsured: '', premium: '', premiumFrequency: 'annual', heldInSuper: false, commencedDate: '', notes: '' };
const toCents = (s: string) => (s.trim() === '' ? null : Math.round(Number(s.replace(/[$,\s]/g, '')) * 100));
const memberName = (m: Member) => [m.firstName, m.surname].filter(Boolean).join(' ').trim() || 'Member';

/** Personal insurance on the household. The start date sets the yearly anniversary shown on Today and in the brief. */
export function InsuranceTab({ clientId, members, canEdit }: { clientId: string; members: Member[]; canEdit: boolean }) {
  const qc = useQueryClient();
  const key = ['client', clientId, 'insurance'];
  const q = useQuery({ queryKey: key, queryFn: () => api<{ data: Policy[] }>(`/api/clients/${clientId}/insurance`) });
  const [editing, setEditing] = useState<{ id: string | null; draft: Draft } | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const adults = members.filter((m) => m.role !== 'dependant');

  if (q.isLoading) return <QASkeleton variant="table" />;
  const rows = q.data?.data ?? [];

  async function save() {
    if (!editing) return;
    const d = editing.draft;
    const body = {
      memberId: d.memberId || null, lifeInsured: d.lifeInsured.trim(), coverType: d.coverType, insurer: d.insurer.trim() || null,
      policyNumber: d.policyNumber.trim() || null, sumInsuredCents: toCents(d.sumInsured), premiumCents: toCents(d.premium),
      premiumFrequency: d.premium.trim() ? d.premiumFrequency : null, heldInSuper: d.heldInSuper, commencedDate: d.commencedDate || null, notes: d.notes.trim() || null,
    };
    if (!body.lifeInsured) { setMsg('Name the life insured.'); return; }
    if ([body.sumInsuredCents, body.premiumCents].some((v) => v != null && (Number.isNaN(v) || v < 0))) { setMsg('Amounts must be numbers.'); return; }
    setBusy(true); setMsg(null);
    try {
      await api(`/api/clients/${clientId}/insurance${editing.id ? `/${editing.id}` : ''}`, { method: editing.id ? 'PATCH' : 'POST', body: JSON.stringify(body) });
      await qc.invalidateQueries({ queryKey: key });
      setEditing(null); setMsg('Saved.');
    } catch (e) { setMsg(e instanceof Error ? e.message : 'Save failed'); } finally { setBusy(false); }
  }
  async function remove(p: Policy) {
    setBusy(true); setMsg(null);
    try {
      await api(`/api/clients/${clientId}/insurance/${p.id}`, { method: 'DELETE' });
      await qc.invalidateQueries({ queryKey: key });
      setMsg('Policy removed.');
    } catch (e) { setMsg(e instanceof Error ? e.message : 'Remove failed'); } finally { setBusy(false); }
  }
  const edit = (p: Policy) => setEditing({ id: p.id, draft: {
    memberId: p.memberId ?? '', lifeInsured: p.lifeInsured, coverType: p.coverType, insurer: p.insurer ?? '', policyNumber: p.policyNumber ?? '',
    sumInsured: p.sumInsuredCents != null ? String(p.sumInsuredCents / 100) : '', premium: p.premiumCents != null ? String(p.premiumCents / 100) : '',
    premiumFrequency: p.premiumFrequency ?? 'annual', heldInSuper: p.heldInSuper, commencedDate: p.commencedDate ?? '', notes: p.notes ?? '',
  } });
  const set = (patch: Partial<Draft>) => setEditing((e) => (e ? { ...e, draft: { ...e.draft, ...patch } } : e));
  const totalCover = (t: CoverType) => rows.filter((r) => r.coverType === t).reduce((s, r) => s + (r.sumInsuredCents ?? 0) / 100, 0);
  const totalPremium = rows.reduce((s, r) => s + (r.annualPremium ?? 0), 0);

  return (
    <div className="space-y-6" data-testid="insurance-tab">
      <section className="qa-card p-5">
        <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
          <div>
            <p className="qa-eyebrow">Personal insurance</p>
            {rows.length > 0 && (
              <p className="text-xs text-ink-mute mt-1">
                {(['life', 'tpd', 'trauma'] as CoverType[]).filter((t) => totalCover(t) > 0).map((t) => `${COVER_LABEL[t]} ${formatAUD(totalCover(t))}`).join(' · ')}
                {totalPremium > 0 && ` · premiums ${formatAUD(totalPremium)} a year`}
              </p>
            )}
          </div>
          {canEdit && !editing && <button type="button" className="btn-primary text-sm" onClick={() => setEditing({ id: null, draft: { ...EMPTY, lifeInsured: adults[0] ? memberName(adults[0]) : '', memberId: adults[0]?.id ?? '' } })}>Add policy</button>}
        </div>
        {rows.length === 0 ? <QAEmpty inline title="No policies recorded">Add cover so its anniversary shows on Today and in the pre-meeting brief.</QAEmpty> : (
          <div className="overflow-x-auto">
            <table className="qa-table text-sm w-full">
              <thead><tr><th>Life insured</th><th>Cover</th><th>Insurer</th><th className="text-right">Sum insured</th><th className="text-right">Premium a year</th><th>In super</th><th>Next anniversary</th>{canEdit && <th />}</tr></thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id}>
                    <td>{p.lifeInsured}</td>
                    <td>{COVER_LABEL[p.coverType] ?? p.coverType}</td>
                    <td>{p.insurer ?? ''}</td>
                    <td className="num">{p.sumInsuredCents != null ? formatAUD(p.sumInsuredCents / 100) : ''}</td>
                    <td className="num">{p.annualPremium != null ? formatAUD(p.annualPremium) : ''}</td>
                    <td>{p.heldInSuper ? 'Yes' : 'No'}</td>
                    <td className="qa-mono text-xs">{p.nextAnniversary ? formatDateAU(p.nextAnniversary) : 'No start date'}</td>
                    {canEdit && (
                      <td className="text-right whitespace-nowrap">
                        <button type="button" className="text-xs text-rust hover:underline mr-3" disabled={busy} aria-label={`Edit ${COVER_LABEL[p.coverType]} for ${p.lifeInsured}`} onClick={() => edit(p)}>Edit</button>
                        <button type="button" className="text-xs text-rust hover:underline" disabled={busy} aria-label={`Remove ${COVER_LABEL[p.coverType]} for ${p.lifeInsured}`} onClick={() => remove(p)}>Remove</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {msg && <p role="status" className="text-xs text-ink-soft mt-3">{msg}</p>}
      </section>

      {editing && (
        <section className="qa-card p-5" aria-label={editing.id ? 'Edit policy' : 'New policy'}>
          <p className="qa-eyebrow mb-4">{editing.id ? 'Edit policy' : 'New policy'}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-xs">
            <label>Life insured
              <select className="qa-input block w-full mt-1 text-sm" aria-label="Household member" value={editing.draft.memberId}
                onChange={(e) => { const m = adults.find((x) => x.id === e.target.value); set({ memberId: e.target.value, ...(m ? { lifeInsured: memberName(m) } : {}) }); }}>
                {adults.map((m) => <option key={m.id} value={m.id}>{memberName(m)}</option>)}
                <option value="">Someone else</option>
              </select>
            </label>
            {!editing.draft.memberId && (
              <label>Name<input className="qa-input block w-full mt-1 text-sm" aria-label="Life insured name" value={editing.draft.lifeInsured} onChange={(e) => set({ lifeInsured: e.target.value })} /></label>
            )}
            <label>Cover
              <select className="qa-input block w-full mt-1 text-sm" aria-label="Cover type" value={editing.draft.coverType} onChange={(e) => set({ coverType: e.target.value as CoverType })}>
                {COVER_TYPES.map((t) => <option key={t} value={t}>{COVER_LABEL[t]}</option>)}
              </select>
            </label>
            <label>Insurer<input className="qa-input block w-full mt-1 text-sm" aria-label="Insurer" value={editing.draft.insurer} onChange={(e) => set({ insurer: e.target.value })} /></label>
            <label>Policy number<input className="qa-input block w-full mt-1 text-sm" aria-label="Policy number" value={editing.draft.policyNumber} onChange={(e) => set({ policyNumber: e.target.value })} /></label>
            <label>Sum insured ($)<input className="qa-input block w-full mt-1 text-sm qa-mono" inputMode="decimal" aria-label="Sum insured" value={editing.draft.sumInsured} onChange={(e) => set({ sumInsured: e.target.value })} /></label>
            <label>Premium ($)<input className="qa-input block w-full mt-1 text-sm qa-mono" inputMode="decimal" aria-label="Premium" value={editing.draft.premium} onChange={(e) => set({ premium: e.target.value })} /></label>
            <label>Paid
              <select className="qa-input block w-full mt-1 text-sm" aria-label="Premium frequency" value={editing.draft.premiumFrequency} onChange={(e) => set({ premiumFrequency: e.target.value as PremiumFrequency })}>
                {PREMIUM_FREQUENCIES.map((f) => <option key={f} value={f}>{PREMIUM_FREQ_LABEL[f]}</option>)}
              </select>
            </label>
            <label>Policy start (sets the anniversary)<input type="date" className="qa-input block w-full mt-1 text-sm" aria-label="Policy start date" value={editing.draft.commencedDate} onChange={(e) => set({ commencedDate: e.target.value })} /></label>
            <label className="flex items-center gap-2 mt-5"><input type="checkbox" checked={editing.draft.heldInSuper} onChange={(e) => set({ heldInSuper: e.target.checked })} />Held inside super</label>
            <label className="sm:col-span-2 lg:col-span-3">Notes<textarea className="qa-input block w-full mt-1 text-sm" rows={2} aria-label="Policy notes" value={editing.draft.notes} onChange={(e) => set({ notes: e.target.value })} /></label>
          </div>
          <div className="flex gap-2 mt-4">
            <button type="button" className="btn-primary text-sm" disabled={busy} onClick={save}>Save policy</button>
            <button type="button" className="btn-ghost text-xs" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </section>
      )}
    </div>
  );
}
